/**
 * Composants React MYRON : encapsulation fine des classes du DS V1.1 (aucune couleur redéfinie).
 * Règle « aucun bouton décoratif » : Button exige onClick, type="submit" ou un lien (to/href via asChild de l'app).
 */
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, Search as SearchIcon, XCircle, type LucideIcon } from 'lucide-react';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');
export { cx };

/** Icône Lucide (hypothèse DO-16 : jeu linéaire cohérent avec .my-icon, trait 1,75). */
export function Icon({ icon: I, label, className }: { icon: LucideIcon; label?: string; className?: string }) {
  return <I className={cx('my-icon', className)} aria-hidden={label ? undefined : true} aria-label={label} strokeWidth={1.75} />;
}

export type VarianteBouton = 'primaire' | 'secondaire' | 'tertiaire' | 'danger';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBouton;
  compact?: boolean;
  chargement?: boolean;
  icone?: LucideIcon;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variante = 'primaire', compact, chargement, icone, className, children, type = 'button', disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('my-btn', `my-btn--${variante}`, compact && 'my-btn--compact', className)}
      aria-busy={chargement || undefined}
      disabled={disabled || chargement}
      {...rest}
    >
      {chargement ? <span className="my-spinner" aria-hidden /> : icone ? <Icon icon={icone} /> : null}
      {children}
    </button>
  );
});

export function Spinner({ label = 'Chargement' }: { label?: string }) {
  return (
    <span role="status" className="my-ligne">
      <span className="my-spinner" aria-hidden />
      <span className="my-sr">{label}</span>
    </span>
  );
}

interface FieldProps {
  label: string;
  aide?: ReactNode;
  erreur?: string | undefined;
  requis?: boolean;
  children: (ids: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
}
export function Field({ label, aide, erreur, requis, children }: FieldProps) {
  const id = useId();
  const aideId = aide ? `${id}-aide` : undefined;
  const errId = erreur ? `${id}-err` : undefined;
  const describedBy = [aideId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="my-field">
      <label className="my-field__label" htmlFor={id}>
        {label}
        {requis && <span className="my-field__requis" aria-hidden>*</span>}
      </label>
      {children({ id, describedBy, invalid: !!erreur })}
      {aide && <span id={aideId} className="my-field__aide">{aide}</span>}
      {erreur && (
        <span id={errId} className="my-field__erreur" role="alert">
          <Icon icon={XCircle} /> {erreur}
        </span>
      )}
    </div>
  );
}

type InputBase = InputHTMLAttributes<HTMLInputElement> & { label: string; aide?: ReactNode; erreur?: string | undefined };
export const TextField = forwardRef<HTMLInputElement, InputBase>(function TextField({ label, aide, erreur, required, ...rest }, ref) {
  return (
    <Field label={label} aide={aide} erreur={erreur} requis={required}>
      {({ id, describedBy, invalid }) => (
        <input ref={ref} id={id} className="my-input" aria-describedby={describedBy} aria-invalid={invalid || undefined} required={required} {...rest} />
      )}
    </Field>
  );
});

type SelectBase = SelectHTMLAttributes<HTMLSelectElement> & { label: string; aide?: ReactNode; erreur?: string | undefined; options: { value: string; label: string }[] };
export const SelectField = forwardRef<HTMLSelectElement, SelectBase>(function SelectField({ label, aide, erreur, options, required, ...rest }, ref) {
  return (
    <Field label={label} aide={aide} erreur={erreur} requis={required}>
      {({ id, describedBy, invalid }) => (
        <select ref={ref} id={id} className="my-input" aria-describedby={describedBy} aria-invalid={invalid || undefined} required={required} {...rest}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
    </Field>
  );
});

type TextareaBase = TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; aide?: ReactNode; erreur?: string | undefined };
export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaBase>(function TextareaField({ label, aide, erreur, required, ...rest }, ref) {
  return (
    <Field label={label} aide={aide} erreur={erreur} requis={required}>
      {({ id, describedBy, invalid }) => (
        <textarea ref={ref} id={id} className="my-input" style={{ paddingTop: 10, minHeight: 96 }} aria-describedby={describedBy} aria-invalid={invalid || undefined} required={required} {...rest} />
      )}
    </Field>
  );
});

export const Choice = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }>(function Choice({ label, type = 'checkbox', ...rest }, ref) {
  return (
    <label className="my-choice">
      <input ref={ref} type={type} {...rest} />
      <span>{label}</span>
    </label>
  );
});

export function SearchField({ label, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return (
    <div className="my-search">
      <Icon icon={SearchIcon} />
      <input type="search" className="my-input" aria-label={label} {...rest} />
    </div>
  );
}

export type TonBadge = 'neutre' | 'succes' | 'alerte' | 'erreur' | 'info' | 'marque' | 'accent' | 'sable';
export function Badge({ ton = 'neutre', children }: { ton?: TonBadge; children: ReactNode }) {
  return <span className={cx('my-badge', ton !== 'neutre' && `my-badge--${ton}`)}>{children}</span>;
}

export type TonAlerte = 'succes' | 'alerte' | 'erreur' | 'info';
const ICONES_ALERTE: Record<TonAlerte, LucideIcon> = { succes: CheckCircle2, alerte: AlertTriangle, erreur: XCircle, info: Info };
export function Alert({ ton = 'info', titre, children, action }: { ton?: TonAlerte; titre?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={cx('my-alert', `my-alert--${ton}`)} role={ton === 'erreur' ? 'alert' : 'status'}>
      <Icon icon={ICONES_ALERTE[ton]} />
      <div className="my-pile my-pile--serre" style={{ flex: 1 }}>
        {titre && <strong>{titre}</strong>}
        {children && <div>{children}</div>}
        {action}
      </div>
    </div>
  );
}

export function Card({ children, className, pad = true, as: As = 'div', ...rest }: { children: ReactNode; className?: string; pad?: boolean; as?: 'div' | 'section' | 'article' } & Record<string, unknown>) {
  return <As className={cx('my-card', pad && 'my-card--pad', className)} {...rest}>{children}</As>;
}

export function Skeleton({ hauteur = 20, largeur = '100%' }: { hauteur?: number; largeur?: number | string }) {
  return <div className="my-skeleton" style={{ height: hauteur, width: largeur }} aria-hidden />;
}
export function SkeletonListe({ lignes = 4 }: { lignes?: number }) {
  return (
    <div className="my-pile" role="status" aria-label="Chargement">
      {Array.from({ length: lignes }, (_, i) => <Skeleton key={i} hauteur={44} />)}
      <span className="my-sr">Chargement en cours</span>
    </div>
  );
}

export function EmptyState({ icone, titre, children, action }: { icone: LucideIcon; titre: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="my-vide">
      <Icon icon={icone} />
      <p className="my-titre-4" style={{ color: 'var(--texte-principal)' }}>{titre}</p>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/** Modale accessible : focus piégé, Échap ferme, retour du focus. */
export function Modal({ ouverte, titre, onFermer, children, actions, large }: { ouverte: boolean; titre: string; onFermer: () => void; children: ReactNode; actions?: ReactNode; large?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const titreId = useId();
  useEffect(() => {
    if (!ouverte) return;
    const precedent = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const focusables = () => Array.from(el?.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') ?? []).filter((e) => !e.hasAttribute('disabled'));
    (focusables()[0] ?? el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onFermer(); }
      if (e.key === 'Tab') {
        const f = focusables();
        if (!f.length) return;
        const premier = f[0]!, dernier = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
        else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); precedent?.focus?.(); };
  }, [ouverte, onFermer]);
  if (!ouverte) return null;
  return (
    <div className="my-scrim my-scrim--fixe" onMouseDown={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div ref={ref} className={cx('my-modal', large && 'my-modal--large')} role="dialog" aria-modal="true" aria-labelledby={titreId} tabIndex={-1}>
        <h2 id={titreId}>{titre}</h2>
        {children}
        {actions && <div className="my-modal__actions">{actions}</div>}
      </div>
    </div>
  );
}
